/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CosmosTabs as Tabs } from "@components/elements";
import { ResellersSearchSectionFragment } from "@cosmos/hygraph-schema";
import { SearchInput } from "@eds-web/react";
import { useMessages } from "@epic-mw/i18n";
import { css } from "@panda/css";
import { HStack } from "@panda/jsx";
import type { ChangeEvent, KeyboardEvent, MouseEvent } from "react";
import { useMemo } from "react";

export type ResellersSearchHeaderProps = Pick<ResellersSearchSectionFragment, "searchLabel"> & {
	searchValue: string;
	currentFilter: string;
	noFilterPath: string;
	searchFilters: { id: string; tabLabel: string }[];
	onValueChange: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
	onFilterChange: (value: string, event?: KeyboardEvent<HTMLAnchorElement> | MouseEvent<HTMLAnchorElement>) => void;
};

const inputStyles = css({
	maxWidth: "400px",
	mdDown: {
		maxWidth: "full",
	},
	"& input": {
		backdropFilter: "blur(75px)",
	},
});

const tabsListStyles = css({
	"& .cosmos-button": {
		paddingInline: "eds.dimension.20",
		borderWidth: "1px",
		lineHeight: "100%",
		minHeight: "eds.dimension.32",
		"&:not(.tab-button-active)": {
			backgroundColor: "eds.palette.transparent.light.005 !important",
			backdropFilter: "blur(75px) !important",
		},
	},
});

export const ResellersSearchHeader = ({
	searchLabel,
	searchValue,
	currentFilter,
	onFilterChange,
	onValueChange,
	searchFilters,
}: ResellersSearchHeaderProps): React.JSX.Element => {
	const messages = useMessages();
	const clearSearchLabel = messages["epic.cosmos.search.clear.button"];
	const tabs = useMemo(() => searchFilters || [], [searchFilters]);

	// Calculate active index from currentFilter (undefined if no filter selected)
	const activeIndex = useMemo(() => {
		if (!currentFilter) return undefined;
		const index = tabs.findIndex((tab) => currentFilter.includes(tab.id));
		return index >= 0 ? index : undefined;
	}, [currentFilter, tabs]);

	// Handle tab change - convert index to filter ID
	const handleTabChange = (value: number | undefined): void => {
		if (value === undefined) {
			// Tab was deselected
			onFilterChange("");
		} else {
			// Tab was selected
			const tabId = tabs[value]?.id;
			if (tabId) {
				onFilterChange(tabId);
			}
		}
	};

	return (
		<HStack
			gap="eds.dimension.24"
			justifyContent="space-between"
			alignItems="center"
			width="full"
			mdDown={{ flexWrap: "wrap" }}
		>
			<Tabs activeIndex={activeIndex} onActiveIndexChange={handleTabChange} canToggleTabs inactiveVariant="outline">
				<Tabs.ButtonList className={tabsListStyles}>
					{tabs.map((tab, index) => (
						<Tabs.Button
							key={tab.id}
							index={index}
							label={tab.tabLabel ?? ""}
							analytics={{
								eventCategory: "button",
								interactionType: "click",
								eventAction: "select",
								eventLabel: tab.tabLabel ?? null,
								eventValue: {
									component: "ResellersSearchHeader",
									filterId: tab.id,
								},
							}}
						/>
					))}
				</Tabs.ButtonList>
			</Tabs>
			<SearchInput
				size="md"
				label={searchLabel || "Search"}
				clearLabel={clearSearchLabel || "Clear search"}
				className={inputStyles}
				value={searchValue}
				onChange={onValueChange}
			/>
		</HStack>
	);
};
